import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSchedule } from '../api';
import { rigaDayKey, rigaTime, TZ } from '../format';

const DAY_RAIL_RADIUS = 5;
const ONE_DAY = 24 * 3600 * 1000;
const WEEKDAY = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'short' });
const DAY_NUM = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, day: 'numeric' });
const MONTH_SHORT = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, month: 'short' });
const DATE_LONG = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'long', day: '2-digit', month: 'short' });

function fmtCountdown(secs: number): string {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(ss)}`;
}

function todayKey(): string {
  return rigaDayKey(new Date().toISOString());
}

function shiftDay(key: string, deltaDays: number): string {
  // Use noon Riga UTC offset rough — sufficient for stepping day keys.
  const [y, m, d] = key.split('-').map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, m - 1, d, 12) + deltaDays * ONE_DAY);
  return rigaDayKey(dt.toISOString());
}

export default function MatchdayBrowser() {
  const { data } = useQuery({ queryKey: ['schedule'], queryFn: getSchedule, refetchInterval: 60_000 });
  const [selectedDay, setSelectedDay] = useState<string>(todayKey);
  const [now, setNow] = useState(Date.now());
  const railRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const allMatches = data?.matches ?? [];
  const countByDay = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of allMatches) {
      const k = rigaDayKey(m.utc_kickoff);
      map.set(k, (map.get(k) ?? 0) + 1);
    }
    return map;
  }, [allMatches]);

  // Build day rail centered on selectedDay
  const railDays = useMemo(() => {
    const out: string[] = [];
    for (let i = -DAY_RAIL_RADIUS; i <= DAY_RAIL_RADIUS; i++) out.push(shiftDay(selectedDay, i));
    return out;
  }, [selectedDay]);

  // Keep selected centered on rail
  useEffect(() => {
    const el = railRef.current?.querySelector('[data-selected="true"]') as HTMLElement | null;
    if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [selectedDay]);

  const matchesForDay = useMemo(() => {
    return allMatches
      .filter((m) => rigaDayKey(m.utc_kickoff) === selectedDay)
      .sort((a, b) => a.utc_kickoff.localeCompare(b.utc_kickoff));
  }, [allMatches, selectedDay]);

  const nextPreId = matchesForDay.find((m) => m.status === 'pre')?.id ?? null;
  const today = todayKey();
  const isToday = selectedDay === today;
  const longLabel = DATE_LONG.format(new Date(`${selectedDay}T12:00:00Z`));

  return (
    <section className="card p-3 sm:p-4 mb-3">
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={() => setSelectedDay(shiftDay(selectedDay, -1))}
          className="h-7 w-7 rounded-md bg-ink-800 hover:bg-ink-700 text-chalk text-sm flex items-center justify-center"
          aria-label="Previous day"
        >‹</button>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <h2 className="text-sm font-semibold truncate">{longLabel}</h2>
            {!isToday && (
              <button
                type="button"
                onClick={() => setSelectedDay(today)}
                className="text-[10px] uppercase tracking-wider text-pitch-400 hover:underline"
              >Today</button>
            )}
          </div>
          <div className="text-[10px] text-dim">{matchesForDay.length} match{matchesForDay.length === 1 ? '' : 'es'}</div>
        </div>
        <button
          type="button"
          onClick={() => setSelectedDay(shiftDay(selectedDay, 1))}
          className="h-7 w-7 rounded-md bg-ink-800 hover:bg-ink-700 text-chalk text-sm flex items-center justify-center"
          aria-label="Next day"
        >›</button>
      </div>

      <div ref={railRef} className="flex gap-1 overflow-x-auto pb-2 mb-3 -mx-1 px-1 snap-x scroll-px-2">
        {railDays.map((dk) => {
          const count = countByDay.get(dk) ?? 0;
          const isSel = dk === selectedDay;
          const isCurrent = dk === today;
          return (
            <button
              key={dk}
              type="button"
              data-selected={isSel}
              onClick={() => setSelectedDay(dk)}
              className={`snap-center shrink-0 w-12 rounded-md p-1.5 text-center transition-colors ${
                isSel ? 'bg-pitch-500/20 ring-1 ring-pitch-500/50'
                      : isCurrent ? 'bg-ink-800 ring-1 ring-ink-700'
                                  : 'bg-ink-800/40 hover:bg-ink-800'
              }`}
            >
              <div className={`text-[10px] uppercase ${isSel ? 'text-pitch-400' : 'text-dim'}`}>{WEEKDAY.format(new Date(`${dk}T12:00:00Z`))}</div>
              <div className={`stat text-base leading-tight ${isSel ? 'text-chalk' : ''}`}>{DAY_NUM.format(new Date(`${dk}T12:00:00Z`))}</div>
              <div className="text-[9px] text-dim">{MONTH_SHORT.format(new Date(`${dk}T12:00:00Z`))}</div>
              <div className={`text-[9px] mt-0.5 font-mono ${count > 0 ? 'text-pitch-400' : 'text-dim/50'}`}>{count > 0 ? `${count}` : '·'}</div>
            </button>
          );
        })}
      </div>

      {matchesForDay.length === 0 ? (
        <div className="text-xs text-dim text-center py-4">No matches scheduled.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {matchesForDay.map((m) => {
            const live = m.status === 'in';
            const done = m.status === 'post';
            const isNextPre = m.id === nextPreId;
            const secs = isNextPre ? (new Date(m.utc_kickoff).getTime() - now) / 1000 : 0;
            const showCountdown = isNextPre && secs > 0 && secs < 24 * 3600 && isToday;
            return (
              <Link
                key={m.id} to={`/matches/${m.id}`}
                className={`rounded-lg px-3 py-2 flex items-center gap-2 ${
                  live ? 'bg-pitch-500/10 ring-1 ring-pitch-500/30'
                       : showCountdown ? 'bg-ink-800/60 ring-1 ring-pitch-500/20 hover:bg-ink-800'
                                       : 'bg-ink-800/60 hover:bg-ink-800'
                }`}
              >
                <div className="flex-1 min-w-0 text-sm">
                  <SideLine flag={m.home_flag} name={m.home_name} owner={m.home_owner} score={(done || live) ? m.home_score : null} />
                  <SideLine flag={m.away_flag} name={m.away_name} owner={m.away_owner} score={(done || live) ? m.away_score : null} />
                </div>
                <div className="text-right shrink-0">
                  {live && <span className="text-[10px] text-pitch-400 font-bold">LIVE</span>}
                  {done && (
                    m.winner === null && m.stage !== 'group'
                      ? <span className="text-[10px] text-amber-400 font-bold" title="Knockout — ET/PK pending">ET/PK</span>
                      : <span className="text-[10px] text-dim">FT</span>
                  )}
                  {!live && !done && (
                    showCountdown ? (
                      <div className="leading-tight">
                        <div className="stat tabular-nums text-sm text-pitch-400">{fmtCountdown(secs)}</div>
                        <div className="text-[10px] text-dim">{rigaTime(m.utc_kickoff)}</div>
                      </div>
                    ) : (
                      <span className="text-xs stat text-dim">{rigaTime(m.utc_kickoff)}</span>
                    )
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

function SideLine({ flag, name, owner, score }: { flag: string | null | undefined; name: string; owner: string | null | undefined; score: number | null | undefined }) {
  return (
    <div className="flex items-center gap-1 truncate">
      <span>{flag ?? '🏳️'}</span>
      <span className="truncate">{name}</span>
      {owner && (
        <span className="ml-1 text-[10px] px-1 rounded bg-ink-700 text-dim shrink-0">@{owner}</span>
      )}
      {score !== null && score !== undefined && <span className="stat ml-auto pl-1">{score}</span>}
    </div>
  );
}
